import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Download, AlertCircle, RefreshCw, ChevronUp, Users, Lock, Eye, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import "./Report.print.css";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExecutiveSummaryCompact } from "@/components/report/ExecutiveSummaryCompact";
import { DecisionSnapshot } from "@/components/report/DecisionSnapshot";
import { ProblemCoreSection } from "@/components/report/ProblemCoreSection";
import { OptionsCompact } from "@/components/report/OptionsCompact";
import { EvaluationMatrix } from "@/components/report/EvaluationMatrix";
import { DominantStrategySection } from "@/components/report/DominantStrategySection";
import { CommitmentBlock } from "@/components/report/CommitmentBlock";
import { ActionPlanSection } from "@/components/report/ActionPlanSection";
import { GoDecisionBlock } from "@/components/report/GoDecisionBlock";
import { ReportPDFPreview } from "@/components/report/ReportPDFPreview";
import { ReportLoadingScreen } from "@/components/report/ReportLoadingScreen";
import { ReportNavigation, REPORT_SECTION_IDS } from "@/components/report/ReportNavigation";
import { DecisionLifecycleBlock } from "@/components/report/DecisionLifecycleBlock";
import { PhaseNavigationDialog } from "@/components/session/PhaseNavigationDialog";
import { GlobalPhaseTimeline } from "@/components/session/GlobalPhaseTimeline";
import { StrategicPathSection } from "@/components/report/StrategicPathSection";
import { StrategicFrameworkSection } from "@/components/report/StrategicFrameworkSection";
import { SuccessCriteriaSection } from "@/components/report/SuccessCriteriaSection";
import { ConflictSection } from "@/components/report/ConflictSection";
import { TeamPerspectivesSection } from "@/components/report/TeamPerspectivesSection";
import { ParticipantsPanel } from "@/components/session/ParticipantsPanel";
import { CollaborationBar } from "@/components/session/CollaborationBar";
import { useSessionSteps } from "@/hooks/useSessionSteps";
import { SourceBadge } from "@/components/report/SourceBadge";
import { ReportVersionHistory } from "@/components/report/ReportVersionHistory";
import { BiasReflectionBlock } from "@/components/report/BiasReflectionBlock";
import { useSessionPresence } from "@/hooks/useSessionPresence";
import { useAuth } from "@/hooks/useAuth";
import quentariLogo from "@/assets/quentari-logo.svg";
// Type definitions in src/types/report.ts for reference.
// Runtime data from Supabase uses `any` at state boundaries due to deeply nested Json fields.

export default function Report() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [needsGeneration, setNeedsGeneration] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [committedOptionName, setCommittedOptionName] = useState<string | undefined>();
  const [alignmentScore, setAlignmentScore] = useState<number | undefined>();
  const [isConstraintBreak, setIsConstraintBreak] = useState(false);
  const [isStrategyShift, setIsStrategyShift] = useState(false);
  const [branding, setBranding] = useState<any>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [sessionMeta, setSessionMeta] = useState<any>(null);
  const [sessionImpactScore, setSessionImpactScore] = useState<number | null>(null);
  const [sessionUrgencyScore, setSessionUrgencyScore] = useState<number | null>(null);
  const [commitRecord, setCommitRecord] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isOutdated, setIsOutdated] = useState(false);
  const [lastSessionUpdate, setLastSessionUpdate] = useState<string | null>(null);
  const [inputsHash, setInputsHash] = useState<string | null>(null);
  const [modelVersion, setModelVersion] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateCooldown, setRegenerateCooldown] = useState(false);
  const [showBackDialog, setShowBackDialog] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("executive-summary");
  const [viewMode, setViewMode] = useState<"executive" | "full">("full");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const { user } = useAuth();
  const { activeUsers, updatePresence } = useSessionPresence(sessionId, user?.id ?? null);
  const { steps } = useSessionSteps(sessionId);
  
  // Broadcast subphase on mount
  useEffect(() => {
    updatePresence({ current_subphase: "report" });
  }, [updatePresence]);

  // Handle scroll to update active section and show scroll-to-top button
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 400);
        
        // Update active section based on scroll position
        const reversed = [...REPORT_SECTION_IDS].reverse();
        for (const sectionId of reversed) {
          const element = document.getElementById(sectionId);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 150) {
              setActiveSection(sectionId);
              break;
            }
          }
        }
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // sticky header + buffer
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - headerOffset, behavior: "smooth" });
    }
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (sessionId) {
      loadReport();
    }
  }, [sessionId]);

  const [outdatedDetails, setOutdatedDetails] = useState<string[]>([]);

  const checkIfReportIsOutdated = async (reportUpdatedAt: string) => {
    try {
      // Hash-based freshness check: compare stored raw_inputs_hash with current data hash
      const { data: latestSnapshot } = await supabase
        .from("report_generation_inputs" as any)
        .select("raw_inputs_hash, input_context")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const storedHash = (latestSnapshot as any)?.raw_inputs_hash;

      if (storedHash) {
        // Build current data hash to compare
        const [sessRes, optRes, matRes, stepsRes] = await Promise.all([
          supabase.from("decision_sessions").select("trigger, stakes, previous_attempts, meta, updated_at").eq("id", sessionId).single(),
          supabase.from("scenarios").select("id, name, updated_at").eq("session_id", sessionId).neq("scenario_type", "baseline"),
          supabase.from("decision_matrix_entries").select("id, criterion, score, weight, updated_at").eq("session_id", sessionId),
          supabase.from("decision_steps").select("id, user_answer, updated_at").eq("session_id", sessionId),
        ]);

        // Determine which categories changed by comparing timestamps
        const reportTime = new Date(reportUpdatedAt).getTime();
        const changes: string[] = [];

        const latestOptUpdate = Math.max(...(optRes.data || []).map((o: any) => new Date(o.updated_at).getTime()), 0);
        const latestMatUpdate = Math.max(...(matRes.data || []).map((m: any) => new Date(m.updated_at).getTime()), 0);
        const latestStepUpdate = Math.max(...(stepsRes.data || []).map((s: any) => new Date(s.updated_at).getTime()), 0);
        const sessUpdate = sessRes.data ? new Date(sessRes.data.updated_at).getTime() : 0;

        if (latestOptUpdate > reportTime) changes.push("Optionen geändert");
        if (latestMatUpdate > reportTime) changes.push("Matrix geändert");
        if (latestStepUpdate > reportTime) changes.push("Phase-1-Antworten geändert");
        if (sessUpdate > reportTime && !changes.length) changes.push("Session-Daten geändert");

        const latestUpdate = Math.max(sessUpdate, latestOptUpdate, latestMatUpdate, latestStepUpdate);
        const outdated = latestUpdate > reportTime;

        setIsOutdated(outdated);
        setOutdatedDetails(changes);
        if (outdated) setLastSessionUpdate(new Date(latestUpdate).toISOString());
        return outdated;
      }

      // Fallback: timestamp-based check
      const { data: sessionData } = await supabase
        .from("decision_sessions").select("updated_at").eq("id", sessionId).single();
      if (!sessionData) return false;
      const sessionUpdate = new Date(sessionData.updated_at).getTime();
      const reportUpdate = new Date(reportUpdatedAt).getTime();
      const outdated = sessionUpdate > reportUpdate;
      setIsOutdated(outdated);
      if (outdated) setLastSessionUpdate(sessionData.updated_at);
      return outdated;
    } catch (error) {
      console.error("Error checking report freshness:", error);
      return false;
    }
  };

  // State for live data from DB
  const [liveOptions, setLiveOptions] = useState<any[]>([]);
  const [liveMatrix, setLiveMatrix] = useState<any[]>([]);

  const loadReport = async () => {
    try {
      setIsLoading(true);
      
      // Load report AND live data in parallel
      const [reportResult, sessionResult, optionsResult, matrixResult] = await Promise.all([
        supabase
          .from("decision_reports")
          .select("*")
          .eq("session_id", sessionId)
          .maybeSingle(),
        supabase
          .from("decision_sessions")
          .select("workspace_id, meta, impact_score, urgency_score")
          .eq("id", sessionId)
          .single(),
        // CRITICAL: Load live options from DB
        supabase
          .from("scenarios")
          .select("id, name, description, archetype, strategic_goal, key_advantages, key_risks, fits_if, does_not_fit_if, scenario_type, order_index, is_constraint_break, is_strategy_shift")
          .eq("session_id", sessionId)
          .neq("scenario_type", "baseline")
          .order("order_index"),
        // CRITICAL: Load live matrix from DB
        supabase
          .from("decision_matrix_entries")
          .select("*")
          .eq("session_id", sessionId)
      ]);

      if (reportResult.error && reportResult.error.code !== "PGRST116") throw reportResult.error;

      // Store live data
      if (optionsResult.data) {
        setLiveOptions(optionsResult.data);
      }
      if (matrixResult.data) {
        setLiveMatrix(matrixResult.data);
      }

      // Extract committed option info from scenarios for loading screen
      if (optionsResult.data && optionsResult.data.length > 0) {
        const meta = sessionResult.data?.meta as any;
        const committedId = meta?.phase3_committed_option_id;
        const committedOpt = committedId
          ? optionsResult.data.find((o: any) => o.id === committedId)
          : optionsResult.data[0];
        if (committedOpt) {
          setCommittedOptionName(committedOpt.name);
          setIsConstraintBreak(!!committedOpt.is_constraint_break);
          setIsStrategyShift(!!committedOpt.is_strategy_shift);
        }
        // Try to get alignment score from meta
        const aScore = meta?.phase3_alignment_score ?? meta?.alignment_score;
        if (aScore != null) setAlignmentScore(aScore);
      }

      if (!reportResult.data) {
        setNeedsGeneration(true);
      } else {
        setReport(reportResult.data);
        await checkIfReportIsOutdated(reportResult.data.updated_at);
      }

      if (sessionResult.data) {
        setWorkspaceId(sessionResult.data.workspace_id);
        setSessionMeta(sessionResult.data.meta);
        setSessionImpactScore((sessionResult.data as any).impact_score ?? null);
        setSessionUrgencyScore((sessionResult.data as any).urgency_score ?? null);
        
        // Load commit record and branding in parallel
        const [{ data: workspaceData }, { data: commitData }] = await Promise.all([
          supabase
            .from("workspaces")
            .select("branding_settings")
            .eq("id", sessionResult.data.workspace_id)
            .single(),
          (supabase.from("decision_commits" as any) as any)
            .select("commit_hash, committed_at, commit_type, participants_snapshot, commit_data, alignment_score_at_commit, decision_thesis_at_commit, session_snapshot, report_id, report_version, input_snapshot_id")
            .eq("session_id", sessionId)
            .order("committed_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);

        if (workspaceData?.branding_settings) {
          setBranding(workspaceData.branding_settings);
        }
        if (commitData) {
          setCommitRecord(commitData);
          // Load input hash from the commit's snapshot
          if (commitData.input_snapshot_id) {
            const { data: inputData } = await supabase
              .from("report_generation_inputs" as any)
              .select("raw_inputs_hash, model, model_version")
              .eq("id", commitData.input_snapshot_id)
              .maybeSingle();
            if (inputData) {
              setInputsHash((inputData as any).raw_inputs_hash);
              setModelVersion((inputData as any).model || (inputData as any).model_version);
            }
          }
        }
        // Fallback: load latest input hash if no commit snapshot reference
        if (!commitData?.input_snapshot_id) {
          const { data: latestInput } = await supabase
            .from("report_generation_inputs" as any)
            .select("raw_inputs_hash, model, model_version")
            .eq("session_id", sessionId)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (latestInput) {
            setInputsHash((latestInput as any).raw_inputs_hash);
            setModelVersion((latestInput as any).model || (latestInput as any).model_version);
          }
        }
      }
    } catch (error) {
      console.error("Error loading report:", error);
      toast({
        title: "Fehler",
        description: "Der Report konnte nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateMatrixCompleteness = async (): Promise<boolean> => {
    try {
      const { data: options, error: optionsError } = await supabase
        .from("scenarios")
        .select("id, name, scenario_type")
        .eq("session_id", sessionId);

      if (optionsError) throw optionsError;
      if (!options || options.length === 0) {
        setValidationErrors(["Keine Optionen vorhanden. Bitte erstellen Sie zunächst Optionen in Phase 2."]);
        return false;
      }

      // Filter out baseline - it's optional for report generation
      const nonBaselineOptions = options.filter(o => o.scenario_type !== 'baseline');
      
      if (nonBaselineOptions.length === 0) {
        setValidationErrors(["Keine strategischen Optionen vorhanden. Bitte erstellen Sie zunächst Optionen in Phase 2."]);
        return false;
      }

      const { data: matrixEntries, error: matrixError } = await supabase
        .from("decision_matrix_entries")
        .select("option_id, criterion, score, weight")
        .eq("session_id", sessionId);

      if (matrixError) throw matrixError;

      const errors: string[] = [];

      if (!matrixEntries || matrixEntries.length === 0) {
        errors.push("Die Decision Matrix ist leer. Bitte bewerten Sie die Optionen in Phase 3.");
        setValidationErrors(errors);
        return false;
      }

      // Extract unique criteria from the actual matrix entries (they are dynamically generated per session)
      const allCriteria = [...new Set(matrixEntries.map(e => e.criterion))];
      
      // Only validate non-baseline options (baseline is optional)
      nonBaselineOptions.forEach(option => {
        const optionEntries = matrixEntries.filter(e => e.option_id === option.id);
        const optionCriteria = optionEntries.map(e => e.criterion);
        
        // Check that this option has entries for all criteria present in the session
        allCriteria.forEach(criterion => {
          if (!optionCriteria.includes(criterion)) {
            errors.push(`Option "${option.name}": Fehlende Bewertung für Kriterium "${criterion}"`);
          }
        });

        // Validate that all entries have scores
        optionEntries.forEach(entry => {
          if (!entry.score || entry.score.trim() === "") {
            errors.push(`Option "${option.name}": Kein Score für Kriterium "${entry.criterion}"`);
          }
        });
      });

      console.log("Matrix validation:", { options: nonBaselineOptions.length, entries: matrixEntries.length, errors });
      setValidationErrors(errors);
      return errors.length === 0;
    } catch (error) {
      console.error("Error validating matrix:", error);
      setValidationErrors(["Fehler bei der Validierung der Matrix-Einträge."]);
      return false;
    }
  };

  const generateReport = async () => {
    if (isRegenerating || regenerateCooldown) return;
    
    setIsRegenerating(true);
    const isValid = await validateMatrixCompleteness();
    
    if (!isValid) {
      setIsRegenerating(false);
      toast({
        title: "Unvollständige Daten",
        description: "Bitte vervollständigen Sie die Decision Matrix, bevor Sie den Report generieren.",
        variant: "destructive",
      });
      return;
    }

    // Delegate to loading screen
    setNeedsGeneration(true);
    
    // Cooldown: prevent re-clicks for 30 seconds
    setRegenerateCooldown(true);
    setTimeout(() => {
      setRegenerateCooldown(false);
      setIsRegenerating(false);
    }, 30000);
  };

  const [targetPhase, setTargetPhase] = useState<number | null>(null);

  const handlePhaseNavigation = (phase: number) => {
    setTargetPhase(phase);
    setShowBackDialog(true);
  };

  const handleConfirmNavigation = async () => {
    if (!sessionId || targetPhase === null) return;
    
    try {
      if (targetPhase === 1) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 1, subphase: "1.4" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}`);
      } else if (targetPhase === 2) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 2, subphase: "2.0" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase2`);
      } else if (targetPhase === 3) {
        await supabase
          .from("decision_sessions")
          .update({ phase: 3, subphase: "3.3" })
          .eq("id", sessionId);
        navigate(`/app/session/${sessionId}/phase3`);
      }
    } catch (error: any) {
      console.error("Error navigating to phase:", error);
      toast({
        title: "Fehler",
        description: "Navigation fehlgeschlagen.",
        variant: "destructive",
      });
    }
  };

  const getPhaseLabel = (phase: number) => {
    const labels: Record<number, string> = {
      1: "Phase 1 – Analyse",
      2: "Phase 2 – Optionen",
      3: "Phase 3 – Bewertung",
      4: "Report"
    };
    return labels[phase] || `Phase ${phase}`;
  };

  const handleReportGenerated = useCallback(async (generatedReport: any) => {
    setReport(generatedReport);
    setNeedsGeneration(false);
    setIsOutdated(false);
    toast({
      title: "Report generiert",
      description: "Der Entscheidungsreport wurde erfolgreich erstellt.",
    });
    // Reload full data (live options, branding etc.)
    await loadReport();
  }, [toast]);

  const handleGenerationError = useCallback((message: string) => {
    toast({
      title: "Fehler",
      description: message,
      variant: "destructive",
    });
  }, [toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Report wird geladen…</p>
        </div>
      </div>
    );
  }

  if (needsGeneration) {
    return <ReportLoadingScreen
      sessionId={sessionId!}
      sessionMeta={sessionMeta}
      committedOptionName={committedOptionName}
      alignmentScore={alignmentScore}
      isConstraintBreak={isConstraintBreak}
      isStrategyShift={isStrategyShift}
      onReportGenerated={handleReportGenerated}
      onError={handleGenerationError}
    />;
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-muted-foreground">
            Kein Report verfügbar. Bitte generiere zuerst einen Report.
          </p>
          <Button onClick={generateReport}>Report generieren</Button>
        </div>
      </div>
    );
  }

  // Extract recommended option label - prioritize from recommendation
  const recommendedOptionLabel = report.recommendation?.recommended_option_label || 
    report.executive_summary?.recommended_option_label || "";

  // CRITICAL: Build options list from LIVE DB data, fallback to report
  const optionsList = liveOptions.length > 0 
    ? liveOptions.map((opt: any) => ({
        option_id: opt.id,
        label: opt.name
      }))
    : (report.options_overview?.options?.map((opt: any) => ({
        option_id: opt.option_id,
        label: opt.label
      })) || []);

  // CRITICAL: Build live options_overview data from DB - NEVER override with stored report data
  const liveOptionsOverview = liveOptions.length > 0 
    ? {
        title: report.options_overview?.title || "Strategische Optionen",
        options: liveOptions.map((opt: any) => {
          // Find AI enrichment from report (only for non-identifying fields)
          const reportOpt = report.options_overview?.options?.find((ro: any) => ro.option_id === opt.id);
          return {
            option_id: opt.id,
            label: opt.name, // ALWAYS from DB
            archetype: opt.archetype, // ALWAYS from DB
            strategic_goal: opt.strategic_goal || opt.description, // ALWAYS from DB
            key_advantages: opt.key_advantages || [], // ALWAYS from DB
            key_risks: opt.key_risks || [], // ALWAYS from DB
            fits_if: opt.fits_if || [],
            does_not_fit_if: opt.does_not_fit_if || [],
            // Only merge AI-enrichment fields that don't override identity
            why_fits: reportOpt?.why_fits,
            when_this_option_fits: reportOpt?.when_this_option_fits,
            hypothesis_link: reportOpt?.hypothesis_link,
          };
        })
      }
    : report.options_overview;

  // CRITICAL: Build live matrix data from DB - match Phase 3 UI scoring (3/2/1)
  const buildLiveMatrixData = () => {
    if (liveMatrix.length === 0) return report.comparison_matrix_summary;

    // Use phase3_criteria from session meta for correct weights and labels
    const phase3Criteria = sessionMeta?.phase3_criteria || [];
    
    // Get unique criteria from matrix entries if no phase3_criteria
    const criteriaFromEntries = [...new Set(liveMatrix.map((e: any) => e.criterion))];
    const allCriteria = phase3Criteria.length > 0 
      ? phase3Criteria.map((c: any) => c.key || c.criterion)
      : criteriaFromEntries;
    
    // K.O.-REGEL: Erstes Kriterium (Index 0) = 50%, Rest = 50% / (n-1)
    const criteriaCount = allCriteria.length;
    
    // Build criteria label map from phase3_criteria
    const criteriaLabelMap: Record<string, string> = {};
    phase3Criteria.forEach((c: any) => {
      criteriaLabelMap[c.key || c.criterion] = c.label || c.key || c.criterion;
    });

    // Helper: compute K.O. weight for a given criterion index
    const getKOWeight = (index: number) => {
      if (criteriaCount <= 1) return 100;
      if (index === 0) return 50;
      const restWeight = Math.floor(50 / (criteriaCount - 1));
      // Adjust remainder on index 1
      if (index === 1) {
        const total = 50 + restWeight * (criteriaCount - 1);
        return restWeight + (100 - total);
      }
      return restWeight;
    };

    // Build matrix view per criterion
    const matrix_view = allCriteria.map((criterion: string, index: number) => {
      const weight = getKOWeight(index);
      
      const criterionEntries = liveMatrix.filter((e: any) => e.criterion === criterion);
      
      // CRITICAL: Use 3/2/1 scale like Phase 3 UI
      const scores = criterionEntries.map((entry: any) => {
        const scoreValue = entry.score?.toLowerCase();
        const scoreNumeric = (scoreValue === 'high' || scoreValue === 'hoch') ? 3 :
          (scoreValue === 'medium' || scoreValue === 'mittel') ? 2 : 1;
        return {
          option_id: entry.option_id,
          score: entry.score || "medium",
          points: scoreNumeric,
          weighted_points: (scoreNumeric * weight) / 100
        };
      });

      return { 
        criterion, 
        label: criteriaLabelMap[criterion] || criterion,
        weight, 
        scores 
      };
    });

    // Calculate totals using 3/2/1 scale
    const optionIds = [...new Set(liveMatrix.map((e: any) => e.option_id))];
    const option_totals = optionIds.map(optionId => {
      const optionEntries = liveMatrix.filter((e: any) => e.option_id === optionId);
      let totalPoints = 0;
      let weightedTotal = 0;

      optionEntries.forEach((entry: any) => {
        const scoreValue = entry.score?.toLowerCase();
        const points = (scoreValue === 'high' || scoreValue === 'hoch') ? 3 :
          (scoreValue === 'medium' || scoreValue === 'mittel') ? 2 : 1;
        const criterionIndex = allCriteria.indexOf(entry.criterion);
        const weight = getKOWeight(criterionIndex);
        totalPoints += points;
        weightedTotal += (points * weight) / 100;
      });

      // Find label from live options
      const optionLabel = liveOptions.find((o: any) => o.id === optionId)?.name || "Option";

      return {
        option_id: optionId,
        label: optionLabel,
        total_points: totalPoints,
        weighted_total: weightedTotal,
        max_points: criteriaCount * 3 // Max is 3 per criterion
      };
    });

    return {
      ...report.comparison_matrix_summary,
      matrix_view,
      option_totals,
      criteria_used: allCriteria,
      criteria_label_map: criteriaLabelMap,
      weighted: true,
      source: "phase3",
      scale: 3 // Indicate 1-3 scale
    };
  };

  const liveMatrixData = buildLiveMatrixData();

  // Extract failure analysis from report (check metadata for v7 structure)
  const failureAnalysis = report.failure_analysis || report.metadata?.failure_analysis || {
    failure_indicators: [],
    pivot_triggers: [],
  };

  // Extract go_decision from metadata if not at top level
  const goDecision = report.go_decision || report.metadata?.go_decision;

  // Extract structured success criteria from session meta (Phase 1.1)
  const successCriteria = {
    signal: sessionMeta?.success_signals?.[0] || report.metadata?.success_signals?.[0],
    timeframe: sessionMeta?.success_timeframe || report.metadata?.success_timeframe,
    validators: sessionMeta?.success_validators || report.metadata?.success_validators || [],
    notes: sessionMeta?.success_notes || report.metadata?.success_notes,
  };

  // Extract Phase 1.2 conflict data from session meta
  const conflictData = {
    strategyA: sessionMeta?.conflict_strategy_a || report.metadata?.conflict_strategy_a,
    strategyB: sessionMeta?.conflict_strategy_b || report.metadata?.conflict_strategy_b,
    costs: sessionMeta?.conflict_costs || report.metadata?.conflict_costs,
  };

  // Extract primary hypothesis for options display
  const primaryHypothesis = report.problem_and_context?.primary_hypothesis;

  // FUNDAMENT 4: Extract evidence map for source badges
  const evidenceMap = report.metadata?.evidence_map || {};

  // Build commitment data from session meta (DecisionCommitDialog speichert in session.meta.decision_commit)
  const sessionCommitment = sessionMeta?.decision_commit || {};
  const reportGoDecision = report.metadata?.go_decision || {};
  
  const commitmentData = {
    recommended_option_id: sessionCommitment.committed_option_id || report.recommendation?.recommended_option_id,
    recommended_option_label: sessionCommitment.committed_option_name || recommendedOptionLabel,
    validity_days: sessionCommitment.validity_days || reportGoDecision.validity_days || 30,
    owner: sessionCommitment.owner_name || sessionCommitment.owner || reportGoDecision.owner,
    review_date: sessionCommitment.review_date || reportGoDecision.review_date,
    committed_at: sessionCommitment.committed_at || reportGoDecision.committed_at,
    excluded_options: sessionCommitment.excluded_options,
    forced_commit: sessionCommitment.forced_commit,
    forced_commit_active_users: sessionCommitment.forced_commit_active_users,
    forced_commit_timestamp: sessionCommitment.forced_commit_timestamp,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b print-hide no-print">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <img 
              src={quentariLogo} 
              alt="Quentari" 
              className="h-8 w-auto cursor-pointer transition-transform duration-300 hover:scale-105" 
              onClick={() => navigate('/dashboard')}
            />
            <div className="h-6 w-px bg-border hidden sm:block" />
            <GlobalPhaseTimeline
              currentPhase={4}
              sessionId={sessionId!}
              onPhaseClick={handlePhaseNavigation}
            />
          </div>
          <div className="flex items-center gap-2">
            {/* Executive / Full Toggle */}
            <div className="hidden sm:flex items-center bg-muted/50 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode("executive")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === "executive"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Eye className="h-3.5 w-3.5" />
                Executive
              </button>
              <button
                onClick={() => setViewMode("full")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === "full"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                Vollständig
              </button>
            </div>
            <CollaborationBar users={activeUsers} steps={steps} currentSubphase="report" />
            {commitRecord && (
              <Badge variant="secondary" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 text-xs">
                ✓ Committed
              </Badge>
            )}
            {!commitRecord && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => generateReport()}
                disabled={isRegenerating || regenerateCooldown}
                className="text-muted-foreground"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{regenerateCooldown ? 'Bitte warten…' : 'Neu generieren'}</span>
              </Button>
            )}
            {(report.locked_at || commitRecord) ? (
              <Button onClick={() => setShowPreview(true)}>
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
            ) : (
              <Button variant="outline" disabled className="opacity-60">
                <Lock className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">PDF (Commit erforderlich)</span>
                <span className="sm:hidden">PDF</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Section Navigation (Desktop) */}
      <ReportNavigation 
        activeSection={activeSection} 
        onNavigate={navigateToSection}
        viewMode={viewMode}
      />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-40 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all print-hide no-print"
          aria-label="Scroll to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}

      {/* Report Content - Single Linear Page */}
      <div className="container mx-auto px-4 py-8 max-w-4xl space-y-8">
        {/* Team Participants */}
        {workspaceId && sessionId && (
          <div className="print-hide no-print flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowParticipants(true)}>
              <Users className="h-4 w-4 mr-2" />
              Team
            </Button>
            <ParticipantsPanel 
              sessionId={sessionId} 
              workspaceId={workspaceId} 
              open={showParticipants}
              onOpenChange={setShowParticipants}
            />
          </div>
        )}

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Matrix-Validierung fehlgeschlagen</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1 mt-2">
                {validationErrors.map((error, index) => (
                  <li key={index} className="text-sm">{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Low-Confidence Warning */}
        {alignmentScore != null && alignmentScore < 60 && (
          <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertTitle>Eingeschränkte Entscheidungsgrundlage</AlertTitle>
            <AlertDescription>
              Der Alignment-Score der empfohlenen Option liegt bei {alignmentScore}% (unter 60%).
              Die Datenlage reicht möglicherweise nicht für eine belastbare Empfehlung.
              Prüfen Sie die Abzüge und erwägen Sie eine erneute Analyse.
            </AlertDescription>
          </Alert>
        )}


        {isOutdated && (
          <Alert variant="default" className="border-warning bg-warning/10">
            <AlertCircle className="h-4 w-4 text-warning" />
            <AlertTitle>Report ist veraltet</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <div>
                <span>
                  Die Daten wurden seit der Generierung aktualisiert
                  {lastSessionUpdate && (
                    <> (zuletzt am {new Date(lastSessionUpdate).toLocaleString("de-DE")})</>
                  )}.
                </span>
                {outdatedDetails.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {outdatedDetails.map((detail, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-warning/20 text-warning-foreground">
                        {detail}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={generateReport}
                disabled={needsGeneration}
                className="ml-4 shrink-0"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Aktualisieren
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* 4.1 Executive Summary */}
        <div>
          <ExecutiveSummaryCompact 
            data={report.executive_summary}
            problemStatement={report.problem_and_context?.problem_statement}
            recommendedOption={recommendedOptionLabel}
            evidence={evidenceMap?.executive_summary}
            decisionQuestion={report.problem_and_context?.decision_question}
          />
          <SourceBadge sources={evidenceMap?.executive_summary?.source_refs} className="mt-2 print-hide no-print" />
        </div>

        {/* Bias Reflexion */}
        <div className={viewMode === "executive" ? "hidden" : ""}>
          <BiasReflectionBlock 
            data={report.metadata?.bias_reflection}
            recommendedOptionLabel={recommendedOptionLabel}
          />
        </div>

        {/* 4.2 Decision Snapshot */}
        <div className={viewMode === "executive" ? "hidden" : ""}>
          <DecisionSnapshot 
            data={report.problem_and_context}
            classificationProvenance={report.metadata?.classification_provenance}
            evidence={evidenceMap?.problem_and_context}
          />
          <SourceBadge sources={evidenceMap?.problem_and_context?.source_refs} className="mt-2 print-hide no-print" />
        </div>

        {/* 4.2a Strategischer Rahmen (Phase-1-Artefakte) */}
        <div className={viewMode === "executive" ? "hidden" : ""}>
          <StrategicFrameworkSection
            sessionMeta={sessionMeta}
            impactScore={sessionImpactScore ?? report.metadata?.user_context?.impact_score ?? null}
            urgencyScore={sessionUrgencyScore ?? report.metadata?.user_context?.urgency_score ?? null}
          />
        </div>

        {/* 4.2b Erfolgskriterien */}
        <div className={viewMode === "executive" ? "hidden" : ""}>
          <SuccessCriteriaSection
            successSignal={successCriteria.signal}
            timeframe={successCriteria.timeframe}
            validators={successCriteria.validators}
            additionalNotes={successCriteria.notes}
          />
        </div>

        {/* 4.2c Zielkonflikt */}
        <div className={viewMode === "executive" ? "hidden" : ""}>
          <ConflictSection
            strategyA={conflictData.strategyA}
            strategyB={conflictData.strategyB}
            costs={conflictData.costs}
          />
        </div>

        {/* 4.2d Team-Perspektiven */}
        <div className={viewMode === "executive" ? "hidden" : ""}>
          <TeamPerspectivesSection
            data={report.metadata?.team_perspectives}
          />
        </div>

        {/* 4.3 Relevant Options - LIVE DATA */}
        <div className={viewMode === "executive" ? "hidden" : ""}>
          <OptionsCompact 
            data={liveOptionsOverview}
            primaryHypothesis={primaryHypothesis}
          />
          <SourceBadge sources={evidenceMap?.options_overview?.source_refs} className="mt-2 print-hide no-print" />
        </div>

        {/* 4.4 Evaluation Matrix - LIVE DATA */}
        <div>
          <EvaluationMatrix 
            data={liveMatrixData}
            options={optionsList}
            recommendedOptionId={report.recommendation?.recommended_option_id}
          />
          <SourceBadge sources={evidenceMap?.comparison_matrix_summary?.source_refs} className="mt-2 print-hide no-print" />
        </div>

        {/* 4.5 Dominant Strategy */}
        <div>
          <DominantStrategySection 
            data={report.recommendation} 
            isConstraintBreak={isConstraintBreak}
            isStrategyShift={isStrategyShift}
            evidence={evidenceMap?.recommendation}
          />
          <SourceBadge sources={evidenceMap?.recommendation?.source_refs} className="mt-2 print-hide no-print" />
        </div>

        {/* 4.5b Strategic Transition Path */}
        <StrategicPathSection 
          data={report.strategic_path || report.metadata?.strategic_path}
          alternativeOptions={liveOptionsOverview?.options?.filter(
            (opt: any) => opt.option_id !== report.recommendation?.recommended_option_id
          )}
          reviewDate={commitmentData.review_date}
        />

        {/* 4.6 Verbindlicher Commit */}
        <CommitmentBlock 
          data={commitmentData}
          recommendedOption={recommendedOptionLabel}
          options={optionsList}
          commitHash={commitRecord?.commit_hash}
          committedAt={commitRecord?.committed_at}
          commitType={commitRecord?.commit_type}
          participantsSnapshot={commitRecord?.participants_snapshot}
          alignmentScoreAtCommit={commitRecord?.alignment_score_at_commit ?? alignmentScore}
          decisionThesisAtCommit={commitRecord?.decision_thesis_at_commit}
          sessionSnapshot={commitRecord?.session_snapshot}
        />

        {/* 4.7 Action Plan */}
        <ActionPlanSection 
          data={report.action_roadmap}
          failureAnalysis={report.failure_analysis}
          sessionId={sessionId}
          workspaceId={workspaceId || undefined}
        />

        {/* 4.8 Go Decision Block */}
        <GoDecisionBlock 
          data={report.go_decision}
          recommendedOption={recommendedOptionLabel}
          validationDays={commitmentData.validity_days || 90}
        />

        {/* Decision Lifecycle */}
        <DecisionLifecycleBlock
          reviewDate={commitmentData.review_date}
          validityDays={commitmentData.validity_days}
          sessionId={sessionId}
          committedAt={commitRecord?.committed_at}
        />

        {/* 4.9 Aggregierte Assumptions */}
        {report.metadata?.assumptions_aggregated && report.metadata.assumptions_aggregated.length > 0 && (
          <div id="assumptions" className={`print-hide no-print ${viewMode === "executive" ? "hidden" : ""}`}>
            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Annahmen-Übersicht
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {report.metadata.assumptions_aggregated.length} Annahmen aus allen Szenario-Analysen
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {report.metadata.assumptions_aggregated.map((a: any, i: number) => (
                  <div key={i} className="flex items-start gap-3 p-2 rounded-lg border border-border/30 bg-muted/20">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
                      {a.option}
                    </span>
                    <span className="text-sm flex-1">{a.assumption}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">
                      {a.status === 'validated' ? '✓ Validiert' : '⚠ Unvalidiert'}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* 4.10 Open Questions / Missing Data */}
        {report.metadata?.open_questions && report.metadata.open_questions.length > 0 && (
          <div id="open-questions" className={`print-hide no-print ${viewMode === "executive" ? "hidden" : ""}`}>
            <Alert variant="default" className="border-amber-200 dark:border-amber-800/30 bg-amber-50/50 dark:bg-amber-950/10">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <AlertTitle>Offene Punkte / Fehlende Daten</AlertTitle>
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1 mt-2">
                  {report.metadata.open_questions.map((q: string, i: number) => (
                    <li key={i} className="text-sm">{q}</li>
                  ))}
                </ul>
                {report.metadata?.data_quality && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Datenqualität:</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {report.metadata.data_quality.completeness_score}%
                    </span>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          </div>
        )}
        {report.id && (
          <div className={`print-hide no-print ${viewMode === "executive" ? "hidden" : ""}`}>
            <ReportVersionHistory
              reportId={report.id}
              currentVersion={report.version_number || 1}
            />
          </div>
        )}
      </div>

      {/* PDF Preview Dialog - with corrected live data + governance props */}
      <ReportPDFPreview
        report={{
          ...report,
          options_overview: liveOptionsOverview,
          comparison_matrix_summary: liveMatrixData
        }}
        branding={branding}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        sessionId={sessionId || ""}
        sessionMeta={sessionMeta}
        reportId={report.id}
        versionNumber={report.version_number || 1}
        commitHash={commitRecord?.commit_hash}
        committedAt={commitRecord?.committed_at}
        commitType={commitRecord?.commit_type}
        inputsHash={inputsHash || undefined}
        modelVersion={modelVersion || undefined}
        isLocked={!!(report.locked_at || commitRecord)}
      />

      {/* Phase Navigation Dialog */}
      <PhaseNavigationDialog
        open={showBackDialog}
        onOpenChange={setShowBackDialog}
        onConfirm={handleConfirmNavigation}
        fromPhase="Report"
        toPhase={getPhaseLabel(targetPhase || 3)}
      />
    </div>
  );
}
